// HAPPYCAP_LOGIN_HIDE_SUBTITLE_MOBILE_V1
import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { loginUser } from "../services/api";
import { useAuth } from "../auth/AuthContext";
import CookieConsent from "../components/CookieConsent";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";

// HAPPYCAP_LOGIN_GRADIENT_BEHIND_NAV_V1
function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState("error");
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!consentGiven || loading || redirecting) return;

    setMsg("");
    setLoading(true);

    try {
      const response = await loginUser(email, password);

      const {
        access,
        refresh,
        user,
        is_active,
        profile_complete,
        activation_required,
      } = response.data;

      if (!is_active) {
        if (activation_required) {
          setMsgType("success");
          setMsg(t("login.sent_activation_code"));

          setTimeout(() => {
            navigate(`/activate?email=${encodeURIComponent(email)}`);
          }, 1500);
        } else {
          setMsgType("error");
          setMsg(t("login.account_not_active"));
        }
        return;
      }

      login(access, refresh, user);

      // HAPPYCAP_LOGIN_DIRECT_REDIRECT_V1
      // Acceso inmediato, sin mensaje duplicado ni espera.
      // Los perfiles incompletos conservan su ruta de finalización.
      const destino = profile_complete ? "/info" : "/edit-profile";
      setRedirecting(true);
      navigate(destino, { replace: true });
    } catch (error) {
      console.error("Error en login:", error.response?.status || "network");

      setMsgType("error");

      if (error.response?.data?.detail) {
        setMsg(error.response.data.detail);
      } else if (error.response?.status === 403) {
        setMsg("CSRF blocked or permission denied.");
      } else {
        setMsg(t("login.error"));
      }
    } finally {
      setLoading(false);
    }
  };

  const disabled = !consentGiven || loading || redirecting;

  return (
    <>
      <CookieConsent onConsentGiven={() => setConsentGiven(true)} />


      {/* HAPPYCAP_LOGIN_PHOTO_REDESIGN_V1 */}
      <main className="relative -mt-16 min-h-screen overflow-hidden bg-[#EAF3FB] pt-16">
        <div className="mx-auto w-full max-w-7xl px-4 pb-10 pt-5 sm:px-6 sm:pt-8 lg:px-8">
          <div className="overflow-hidden rounded-[26px] border border-white/80 bg-white shadow-[0_16px_55px_rgba(22,63,99,0.09)] lg:grid lg:min-h-[650px] lg:grid-cols-[1.05fr_1fr]">

            {/* Fotografía y mensaje de bienvenida */}
            <section
              className="relative h-48 overflow-hidden bg-[#DCECF9] sm:h-64 lg:h-auto lg:min-h-[650px]"
              aria-label="La communauté HAPPYCAP"
            >
              <img
                src="/img/happycap-login-photo.jpg"
                alt="Amigos conversando y compartiendo un momento agradable"
                className="absolute inset-0 h-full w-full object-cover object-center"
              />

              <div
                className="absolute inset-0 bg-gradient-to-t from-[#102E49]/80 via-[#102E49]/20 to-transparent"
                aria-hidden="true"
              />

              <div className="absolute bottom-0 left-0 right-0 p-5 text-white sm:p-8 lg:p-10">
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-white/90 sm:text-sm">
                  HAPPYCAP.eu
                </p>

                <h1 className="max-w-lg text-2xl font-bold leading-tight tracking-tight sm:text-3xl lg:text-4xl">
                  Des liens qui nous
                  <span className="text-[#FFB15E]"> rapprochent.</span>
                </h1>

                <p className="mt-3 hidden max-w-md text-base leading-relaxed text-white/95 sm:block">
                  Partagez, échangez, avancez ensemble dans une communauté qui vous comprend.
                </p>
              </div>
            </section>

            {/* Formulario existente: lógica de acceso intacta */}
            <section className="flex items-center justify-center px-5 py-8 sm:px-10 sm:py-12 lg:px-12">
              <div className="w-full max-w-md">

                <div className="mb-8 text-center">
                  <p className="text-sm font-bold uppercase tracking-[0.15em] text-[#247BA8]">
                    HAPPYCAP.eu
                  </p>
                  <h2
                    id="login-title"
                    className="mt-3 text-3xl font-bold tracking-tight text-[#163F63] sm:text-4xl"
                  >
                    Bienvenue !
                  </h2>
                  <p className="hidden md:block mt-3 text-sm leading-relaxed text-slate-500 sm:text-base">
                    Connectez-vous pour retrouver votre communauté.
                  </p>
                </div>

                <div className="rounded-2xl">
<form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label
                  htmlFor="email"
                  className="mb-2 block text-base font-medium text-slate-700"
                >
                  {t("login.email")}
                </label>

                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  disabled={disabled}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base font-normal text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-[#247BA8] focus:ring-2 focus:ring-[#247BA8] disabled:cursor-not-allowed disabled:opacity-60"
                  autoFocus
                />
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <label
                    htmlFor="password"
                    className="block text-base font-medium text-slate-700"
                  >
                    {t("login.password")}
                  </label>

                  <button
                    type="button"
                    onClick={() => navigate("/reset-password")}
                    disabled={disabled}
                    className="text-xs font-semibold text-sky-700 transition hover:text-sky-900 hover:underline disabled:opacity-50"
                  >
                    {t("login.reset_password")}
                  </button>
                </div>

                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    disabled={disabled}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 pr-12 text-base font-normal text-slate-700 placeholder:text-slate-400 outline-none transition focus:border-[#247BA8] focus:ring-2 focus:ring-[#247BA8] disabled:cursor-not-allowed disabled:opacity-60"
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    disabled={disabled}
                    aria-label={t("login.password")}
                    aria-pressed={showPassword}
                    className="absolute inset-y-0 right-3 my-auto h-fit rounded-md px-2 py-1 text-xs font-semibold text-sky-700 hover:text-sky-900 disabled:opacity-50"
                  >
                    {showPassword ? (
                      <EyeOff size={20} aria-hidden="true" />
                    ) : (
                      <Eye size={20} aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={disabled}
                className="flex w-full items-center justify-center rounded-xl border-2 border-orange-400 bg-transparent px-5 py-3.5 text-base font-semibold text-orange-500 shadow-sm transition-all duration-200 hover:bg-orange-50 hover:shadow-md focus:outline-none focus:ring-4 focus:ring-orange-200 disabled:cursor-not-allowed disabled:border-slate-300 disabled:text-slate-400 disabled:shadow-none"
              >
                {loading ? "..." : t("login.submit")}
              </button>

              {msg && (
                <p
                  role="alert"
                  aria-live="assertive"
                  className={`rounded-xl px-4 py-3 text-center text-sm font-medium ${
                    msgType === "error"
                      ? "bg-red-50 text-red-700"
                      : "bg-green-50 text-green-700"
                  }`}
                >
                  {msg}
                </p>
              )}

              <div className="border-t border-slate-100 pt-5 text-center">
                <button
                  type="button"
                  onClick={() => navigate("/signup")}
                  disabled={disabled}
                  className="text-sm font-semibold text-sky-700 transition hover:text-sky-900 hover:underline disabled:opacity-50"
                >
                  {t("login.no_account")}
                </button>
              </div>
            </form>
                </div>

              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer loginStyle />
    </>
  );
}

export default Login;
